/**
 * Behavioral Pattern Learning Types
 *
 * These types support the AI-powered behavioral learning system that:
 * - Records successful workflows and action patterns
 * - Recalls similar patterns based on current context
 * - Suggests next actions based on learned behaviors
 *
 * The system stores trigger embeddings for semantic similarity search,
 * enabling the SDK to anticipate what actions users might want to take.
 */

import { z } from "zod";

export const BehaviorOutcomeValues = ["success", "partial", "failed"] as const;
export type BehaviorOutcome = typeof BehaviorOutcomeValues[number];

// ============================================================================
// Behavior Pattern Core Types
// ============================================================================

/**
 * A single action within a behavior pattern
 */
export interface BehaviorAction {
  /** Tool or action identifier */
  tool: string;

  /** Parameters used with this action */
  parameters?: Record<string, unknown>;

  /** Legacy alias for parameters; use `parameters` for new callers */
  params?: Record<string, unknown>;

  /** Outcome of the action when recorded as a workflow step */
  outcome?: BehaviorOutcome;

  /** Timestamp when action occurred */
  timestamp?: string;

  /** Duration in milliseconds (if applicable) */
  duration_ms?: number;
}

/**
 * Context that was active when a behavior pattern was recorded
 */
export interface BehaviorContext {
  /** Current project or workspace */
  project?: string;

  /** Files that were open/active */
  active_files?: string[];

  /** Current task or goal description */
  current_task?: string;

  /** Environment metadata (IDE, terminal, etc.) */
  environment?: string;

  /** Additional custom context */
  [key: string]: unknown;
}

/**
 * A recorded behavior pattern with embeddings for similarity search
 */
export interface BehaviorPattern {
  /** Unique identifier */
  id: string;

  /** User who created this pattern */
  user_id: string;

  /** Natural language description of what triggered this pattern */
  trigger: string;

  /** OpenAI-compatible trigger embedding for semantic search */
  trigger_embedding?: number[];

  /** Voyage trigger embedding for semantic search */
  voyage_trigger_embedding?: number[];

  /** Context when pattern was recorded */
  context: BehaviorContext;

  /** Ordered list of actions taken */
  actions: BehaviorAction[];

  /** Final outcome/result classification */
  final_outcome: BehaviorOutcome;

  /** Confidence score (0-1) indicating pattern reliability */
  confidence: number;

  /** Number of times this pattern has been recalled/used */
  use_count: number;

  /** Similarity score (only present in search results) */
  similarity?: number;

  /** When the pattern was created */
  created_at: string;

  /** When the pattern was last updated/used */
  updated_at: string;
}

// ============================================================================
// Record Behavior Endpoint Types
// ============================================================================

/**
 * Parameters for recording a new behavior pattern
 */
export interface RecordBehaviorParams {
  /** User ID (UUID) */
  user_id: string;

  /** Natural language description of what triggered this workflow */
  trigger: string;

  /** Context when the pattern occurred */
  context?: BehaviorContext;

  /** Ordered list of actions taken */
  actions: BehaviorAction[];

  /** Final outcome classification */
  final_outcome: BehaviorOutcome;

  /** Confidence in this pattern (0-1, default: 0.7) */
  confidence?: number;
}

/** Zod schema for API validation */
export const RecordBehaviorParamsSchema = z.object({
  user_id: z.string().uuid(),
  trigger: z.string().min(1, "Trigger description is required"),
  context: z.record(z.unknown()).optional(),
  actions: z.array(z.object({
    tool: z.string(),
    parameters: z.record(z.unknown()).optional(),
    params: z.record(z.unknown()).optional(),
    outcome: z.enum(BehaviorOutcomeValues),
    timestamp: z.string().optional(),
    duration_ms: z.number().optional(),
  })).min(1, "At least one action is required"),
  final_outcome: z.enum(BehaviorOutcomeValues),
  confidence: z.number().min(0).max(1).default(0.7),
});

/**
 * Response from recording a behavior pattern
 */
export interface RecordBehaviorResult {
  /** The created or updated pattern */
  pattern: BehaviorPattern;

  /** Whether a duplicate was updated instead of creating new */
  was_duplicate: boolean;

  /** Message describing what happened */
  message: string;
}

// ============================================================================
// Recall Behavior Endpoint Types
// ============================================================================

/**
 * Parameters for recalling similar behavior patterns
 */
export interface RecallBehaviorParams {
  /** User ID (UUID) */
  user_id: string;

  /** Current context for similarity matching */
  context: {
    /** Current task description (required for embedding generation) */
    current_task: string;

    /** Optional additional context fields */
    [key: string]: unknown;
  };

  /** Maximum patterns to return (default: 5) */
  limit?: number;

  /** Minimum similarity threshold (0-1, default: 0.7) */
  similarity_threshold?: number;
}

/** Zod schema for API validation */
export const RecallBehaviorParamsSchema = z.object({
  user_id: z.string().uuid(),
  context: z.object({
    current_task: z.string().min(1, "Current task description is required"),
  }).passthrough(),
  limit: z.number().int().min(1).max(20).default(5),
  similarity_threshold: z.number().min(0).max(1).default(0.7),
});

/**
 * A recalled pattern with similarity information
 */
export interface RecalledPattern {
  /** The matched pattern */
  pattern: BehaviorPattern;

  /** Similarity score (0-1) */
  similarity_score: number;

  /** Human-readable explanation of why this matched */
  relevance_reason: string;
}

/**
 * Response from recalling behavior patterns
 */
export interface RecallBehaviorResult {
  /** Matched patterns sorted by similarity */
  patterns: RecalledPattern[];

  /** Total patterns found */
  total_found: number;
}

// ============================================================================
// Suggest Action Endpoint Types
// ============================================================================

/**
 * Parameters for getting action suggestions
 */
export interface SuggestActionParams {
  /** User ID (UUID) */
  user_id: string;

  /** Current state for action prediction */
  current_state: {
    /** Description of current task */
    task_description: string;

    /** Steps already completed (for predicting next step) */
    completed_steps?: string[];

    /** Optional additional state */
    [key: string]: unknown;
  };

  /** Maximum suggestions to return (default: 3) */
  max_suggestions?: number;
}

/** Zod schema for API validation */
const SuggestActionStepSchema = z
  .union([
    z.string().min(1, "Completed step must be a non-empty string"),
    z.object({
      tool: z.string().min(1, "Legacy step objects must include tool"),
      parameters: z.record(z.unknown()).optional(),
      params: z.record(z.unknown()).optional(),
    }),
  ])
  .transform((step) => (typeof step === "string" ? step : step.tool));

export const SuggestActionParamsSchema = z.object({
  user_id: z.string().uuid(),
  current_state: z.object({
    task_description: z.string().min(1, "Task description is required"),
    completed_steps: z.array(SuggestActionStepSchema).optional(),
  }).passthrough(),
  max_suggestions: z.number().int().min(1).max(10).default(3),
});

/**
 * A suggested next action based on learned patterns
 */
export interface ActionSuggestion {
  /** Suggested action description */
  action: string;

  /** Tool to use for this action */
  tool: string;

  /** Confidence score (0-1) */
  confidence: number;

  /** Pattern IDs that informed this suggestion */
  based_on_patterns: string[];

  /** Human-readable explanation */
  reasoning: string;
}

/**
 * Response from action suggestion endpoint
 */
export interface SuggestActionResult {
  /** Suggested actions sorted by confidence */
  suggestions: ActionSuggestion[];
}

// ============================================================================
// Edge Function Response Wrapper
// ============================================================================

/**
 * Standard response format from behavior edge functions
 */
export interface BehaviorEdgeFunctionResponse<T> {
  /** Whether the operation succeeded */
  success: boolean;

  /** Response data (present on success) */
  data?: T;

  /** Error information (present on failure) */
  error?: {
    message: string;
    code: string;
  };

  /** Optional message (for duplicate detection, etc.) */
  message?: string;
}
